import { useMemo } from 'react';

import { Button, Input, Pagination, Spin, Tooltip } from 'antd';
import { useIntl } from 'react-intl';

import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import { ResponsiveTable } from '@src/components/Table';
import { useRulesByCountry } from '@src/packages/utils/hooks/useRulesByCountry';
import { formatUserFullName } from '@src/util/format/userFullName';
import IntlMessages from '@src/util/IntlMessages';

import { MarkAttendanceJourneyLabel } from './MarkAttendanceJourneyLabel';
import type { MarkAttendanceUserRow } from '../types';

import styles from '../MarkAttendanceDrawer.module.less';

type MarkAttendanceUserStepProps = {
  users: MarkAttendanceUserRow[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onSelectUser: (user: MarkAttendanceUserRow) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
};

const MarkAttendanceUserStep = ({
  users,
  loading,
  search,
  onSearchChange,
  onSelectUser,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
}: MarkAttendanceUserStepProps) => {
  const intl = useIntl();
  const { countryRulesForm } = useRulesByCountry();
  const identifierLabel = intl.formatMessage({
    id: countryRulesForm?.identifier?.label ?? 'country.chile.identifier',
  });

  const columns = useMemo(
    () => [
      {
        key: 'user',
        dataIndex: 'id',
        title: intl.formatMessage({ id: 'workJourney.realTime.markAttendance.column.user' }),
        align: 'left' as const,
        render: (row: MarkAttendanceUserRow) => (
          <div className={styles.userCell}>
            <MemberAvatar user={row} size={32} />
            <span className={styles.userName}>{formatUserFullName(row)}</span>
          </div>
        ),
      },
      {
        key: 'idCard',
        dataIndex: 'idCard',
        title: identifierLabel,
        align: 'left' as const,
        render: (row: MarkAttendanceUserRow) => {
          if (!row.idCard) {
            return '—';
          }
          return countryRulesForm?.identifier?.formatIdentifier(row.idCard) ?? row.idCard;
        },
      },
      {
        key: 'email',
        dataIndex: 'email',
        title: intl.formatMessage({ id: 'workJourney.realTime.markAttendance.column.email' }),
        align: 'left' as const,
        width: 160,
        render: (row: MarkAttendanceUserRow) => {
          const email = row.email?.trim();
          if (!email) {
            return '—';
          }
          return (
            <Tooltip title={email}>
              <span className={styles.emailCell}>{email}</span>
            </Tooltip>
          );
        },
      },
      {
        key: 'journey',
        dataIndex: 'journeyName',
        title: intl.formatMessage({ id: 'workJourney.columns.journey' }),
        align: 'left' as const,
        render: (row: MarkAttendanceUserRow) => (
          <MarkAttendanceJourneyLabel journeyName={row.journeyName} />
        ),
      },
      {
        key: 'action',
        dataIndex: 'id',
        title: '',
        align: 'center' as const,
        width: 110,
        render: (row: MarkAttendanceUserRow) => (
          <Button
            type="primary"
            size="small"
            className="gx-mb-0"
            onClick={() => onSelectUser(row)}
          >
            <IntlMessages id="workJourney.realTime.markAttendance.markUser" />
          </Button>
        ),
      },
    ],
    [countryRulesForm, identifierLabel, intl, onSelectUser],
  );

  return (
    <div className={styles.stepBody}>
      <p className={styles.stepHint}>
        <IntlMessages id="workJourney.realTime.markAttendance.selectUserHint" />
      </p>
      <Input
        allowClear
        value={search}
        onChange={event => onSearchChange(event.target.value)}
        placeholder={intl.formatMessage({
          id: 'workJourney.realTime.markAttendance.searchPlaceholder',
        }, { identifier: identifierLabel })}
        prefix={<span className="material-icons-outlined">search</span>}
        className={styles.searchInput}
      />
      <Spin spinning={loading}>
        <ResponsiveTable columns={columns} dataSource={users} type="light" />
        <div className="gx-d-flex gx-justify-content-center gx-mt-3">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      </Spin>
    </div>
  );
};

export default MarkAttendanceUserStep;
